<script>
import CompanyEventsFilters from '@events/components/events-company-filters.vue'
import EventsParentMixin from '@events/mixins/events-parent-mixin'
import Layout from '@layouts/loggedin.vue'
import store from '@state/store'
import { authComputed } from '@state/helpers'
import { debounce } from 'debounce'
import { parseISO } from 'date-fns'
import { isFuture, isPast } from 'date-fns/esm'
import { convertToCSV } from '@events/utils'

export default {
  name: 'CompanyEvents',
  components: { Layout, CompanyEventsFilters },
  mixins: [EventsParentMixin],
  props: {
    companyId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      currentDate: new Date(),
      eventsPage: 0,
      eventsPerPage: 12,
      singleEvent: false,
    }
  },
  computed: {
    company() {
      return this.currentUser.companies.find(({ id }) => id === this.companyId)
    },
    currentCompany() {
      let currentCompany = false
      for (let index = 0; index < this.currentUser.companies.length; index++) {
        if (this.currentUser.companies[index].id === this.companyId) {
          currentCompany = this.currentUser.companies[index]
        }
      }
      return currentCompany
    },
    allEvents() {
      const events = this.$store.state.events.events || []
      if (!events.length) return events

      const { name } = this.$route

      if (name === 'companyEvents') {
        return events.filter(({ end }) => isFuture(parseISO(end)))
      }

      if (name === 'myEvents') {
        return events.filter(
          ({ user_id: userId, leaders, users }) =>
            userId === this.currentUserId ||
            !!leaders.find(({ id }) => id === this.currentUserId) ||
            !!users.find(({ id }) => id === this.currentUserId)
        )
      }

      if (name === 'pastEvents') {
        return events.filter(({ end }) => isPast(parseISO(end)))
      }

      return events
    },
    reachedLastPage() {
      return this.maxEventsPages - this.eventsPage - 1 === 0
    },
    events() {
      const endIndex = (this.eventsPage + 1) * this.eventsPerPage
      return this.allEvents.slice(0, endIndex)
    },
    maxEventsPages() {
      return Math.ceil(this.allEvents.length / this.eventsPerPage)
    },
    ...authComputed,
  },
  methods: {
    nextPage() {
      if (!this.reachedLastPage) {
        this.eventsPage++
      }
    },
    openEventModal(oneEvent) {
      this.singleEvent = oneEvent
      // HACK: show url for the event /company/:companyId/events/:eventId
      window.history.pushState(
        {},
        null,
        window.location.pathname.replace('/events', '') +
          '/events/' +
          oneEvent.id
      )
    },
    closeModal() {
      this.singleEvent = null
      window.history.back()
    },
    // Call only every 500ms so we don't update imediatelly if the user is browsing
    updateMonth: debounce(async function updateMonth(date) {
      this.currentDate = date
      await store.dispatch('events/setFilterMonth', {
        companyId: this.company.id,
        monthDate: date,
      })
    }, 500),
    set10DaysOpenFilter() {
      this.$refs.companyEventsFilters.set10DaysOpenFilter()
    },
    exportEvents() {
      const csvString = convertToCSV(this.allEvents)

      const fileName = `${this.$route.name}.csv`
      if (window.navigator.msSaveOrOpenBlob) {
        // IE
        const blob = new Blob([csvString])
        window.navigator.msSaveOrOpenBlob(blob, fileName)
      } else {
        const a = document.createElement('a')
        a.href = 'data:attachment/csv,' + encodeURIComponent(csvString)
        a.target = '_blank'
        a.download = fileName
        document.body.appendChild(a)
        a.click()
        setTimeout(() => a.remove(), 2000)
      }
    },
  },
}
</script>

<template>
  <Layout>
    <div
      v-infinite-scroll="nextPage"
      infinite-scroll-disabled="reachedLastPage"
    >
      <div class="bg-white">
        <div class="flex justify-between pl-10 pr-12 h-27 leading-27">
          <div class="">
            <div
              class="inline-block overflow-hidden align-middle bg-white rounded-full w-18 h-18 shadow-input"
            >
              <img :src="company.image_url" />
            </div>
            <div class="inline-block ml-5 align-middle">
              <h2 class="text-2xl text-blue-darker">{{ company.name }}</h2>
            </div>
          </div>
          <div class="align-middle">
            <router-link
              v-if="canEditCompany(currentCompany.id)"
              class="inline-block w-48 h-12 mb-4 text-right button primary-button"
              :to="{ name: 'createNewEvent' }"
            >
              + Create An Event
            </router-link>
          </div>
        </div>

        <CompanyEventsFilters
          ref="companyEventsFilters"
          :company-id="companyId"
        />

        <div class="flex justify-between pl-10 pr-12 mt-3 h-14 leading-14">
          <div class="flex">
            <BaseLink
              name="companyEvents"
              class="events-tab"
              active-class="active"
              append
              exact
            >
              Discover
            </BaseLink>
            <BaseLink
              name="myEvents"
              class="events-tab"
              active-class="active"
              append
            >
              My Volunteer Opportunities
            </BaseLink>
            <BaseLink
              name="pastEvents"
              class="events-tab"
              active-class="active"
              append
            >
              Past
            </BaseLink>
          </div>
          <div class="space-x-10 align-middle">
            <button
              class="inline-block mb-4 text-base text-left button secondary-button"
              @click="set10DaysOpenFilter"
            >
              Occurring Soon
            </button>

            <button
              class="inline-block mb-4 text-base text-left button secondary-button"
              @click="exportEvents"
            >
              Export Volunteer Opportunities Details
            </button>
          </div>
        </div>
      </div>
      <RouterView :key="$route.fullPath" />
    </div>
  </Layout>
</template>
